/**
 * Saving a chart as a template, and applying one to a chart.
 *
 * Two dialogs in one module because they are two halves of one idea and share
 * the decision that shapes both: **the symbol is chosen at save time and
 * *offered* at apply time.** The person saving knows whether the symbol is the
 * point — a club's "Breadth" chart is about `SPY` and is nonsense on anything
 * else — and the person applying does not.
 *
 * ## Reached from the chart's own right-click menu
 *
 * Which also settles what "this chart" means. A strip key would drive the
 * *active* chart — the focus ring the indicator picker follows — and the dialog
 * would need a sentence saying which one that is. Right-clicking names the
 * chart by pointing at it, so "Apply to this chart" is literally true.
 *
 * ## Both warnings are conditional, and that is the point
 *
 * Applying can move every linked chart, and it can take drawings with it.
 * Neither line is rendered unless it is about to happen. A warning that appears
 * when nothing will happen is one people learn to click past, and it spends the
 * attention the real one needs.
 */

import { Fragment, useEffect, useMemo, useState } from "react";
import type { ChartGroup } from "@qufolio/model";

import {
  createChartTemplate,
  deleteChartTemplate,
  fetchChartTemplate,
  fetchChartTemplates,
  publishChartTemplate,
  saveChartTemplate,
  type ChartTemplateSummary,
} from "../api/chart-templates";
import { PublishedMenu } from "./PublishedMenu";
import { applyTo, definitionOf, drawingsKept, movesLinkedCharts } from "../lib/chart-template";

// ── Save ─────────────────────────────────────────────────────────────────────

export interface SaveChartTemplateProps {
  group: ChartGroup;
  /** The club this account may publish to, or absent for no publish option. */
  publishTarget?: string | null;
  /** The group as the author reads it — `JP`, not `grp-jp`. Falls back to the key. */
  publishTargetName?: string | null;
  onSaved: (summary: ChartTemplateSummary) => void;
  onClose: () => void;
}

export function SaveChartTemplate({
  group,
  publishTarget,
  publishTargetName,
  onSaved,
  onClose,
}: SaveChartTemplateProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [keepSymbol, setKeepSymbol] = useState(true);
  const [share, setShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Your own templates, so a name you already use can be replaced rather than refused. */
  const [mine, setMine] = useState<ChartTemplateSummary[]>([]);

  useEffect(() => {
    let live = true;
    void fetchChartTemplates().then((found) => {
      if (live) setMine(found.filter((t) => t.ownerKind === "user"));
    });
    return () => {
      live = false;
    };
  }, []);

  const panes = group.panes.length;

  /**
   * The one of yours this name would replace, if any.
   *
   * **Saving under a name you already use replaces that template**, because
   * that is what re-saving means — and the only alternative the server has is
   * refusing, which left deleting the old one first as the way to update a
   * template. The Save button says "Replace" when this is set, so the label is
   * the warning and there is no second dialog to click through. Exact match,
   * as the server's uniqueness is: "momentum" beside "Momentum" is a new one.
   */
  const existing = mine.find((t) => t.name === name.trim()) ?? null;

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const definition = definitionOf(group, { keepSymbol });
      const input = {
        name: trimmed,
        // A replace is whole on the server, so an empty box would clear what
        // the old one was filed under. Kept unless a new one is typed.
        category: category.trim() || existing?.category || undefined,
        description: existing?.description ?? undefined,
        definition,
        keepSymbol,
      };
      const saved = existing
        ? await saveChartTemplate(existing.key, input)
        : await createChartTemplate(input);
      // **Published as well as kept, never instead of.** Sharing a chart you do
      // not keep leaves you unable to edit what the club now has — the club's
      // copy is theirs, and yours is the one the picker lets you change.
      if (share && publishTarget) {
        await publishChartTemplate({
          name: trimmed,
          ownerKey: publishTarget,
          definition,
          keepSymbol,
        });
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      // Shown, never swallowed: somebody who pressed Save and saw the dialog
      // close would believe it worked. `api/chart-templates.ts` throws for
      // exactly this reason.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Backdrop onClose={onClose}>
      <div style={PANEL}>
        <div style={HEAD}>Save chart as template</div>
        <div style={BODY}>
          <Field label="Name">
            <input
              autoFocus
              aria-label="Template name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
              }}
              style={INPUT}
            />
          </Field>
          <Field label="Category">
            <input
              aria-label="Template category"
              placeholder="optional"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={INPUT}
            />
          </Field>

          <Tick
            on={keepSymbol}
            onToggle={() => setKeepSymbol((v) => !v)}
            label={`Save the symbol (${group.symbol})`}
            hint="Applying then offers it. Off, the template carries the panes and indicators only."
          />

          {publishTarget && (
            <Tick
              on={share}
              onToggle={() => setShare((v) => !v)}
              label={`Share with "${publishTargetName ?? publishTarget}" Group`}
              hint="Members get it in their picker straight away — publishing issues the grant with it."
            />
          )}

          <div style={NOTE}>
            {panes} pane{panes === 1 ? "" : "s"} · {group.frequency}
            {group.barSource ? ` · ${group.barSource}` : ""}
          </div>

          {error && <div style={ERROR}>{error}</div>}

          <div style={ACTIONS}>
            <button type="button" onClick={onClose} style={BUTTON}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!name.trim() || busy}
              title={existing ? `Overwrites your saved "${existing.name}"` : undefined}
              style={{
                ...BUTTON,
                ...PRIMARY,
                ...LABEL_FITS,
                ...(name.trim() && !busy ? {} : DISABLED),
              }}
            >
              {busy ? "Saving…" : existing ? `Replace "${existing.name}"` : "Save"}
            </button>
          </div>
        </div>
      </div>
    </Backdrop>
  );
}

// ── Apply ────────────────────────────────────────────────────────────────────

export interface ApplyChartTemplateProps {
  group: ChartGroup;
  /** Whether this layout links symbols, and across how many charts. */
  symbolLinked: boolean;
  chartsInLayout: number;
  onApply: (group: ChartGroup) => void;
  onClose: () => void;
  /**
   * The group being authored for, while acting as its account. Its templates
   * then carry a History toggle — versions, Restore, Withdraw is elsewhere —
   * and nobody else's do (`docs/plans/group-accounts.md` §5).
   */
  authoringFor?: { group: string; name: string };
}

export function ApplyChartTemplate({
  group,
  symbolLinked,
  chartsInLayout,
  onApply,
  onClose,
  authoringFor,
}: ApplyChartTemplateProps) {
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [rows, setRows] = useState<ChartTemplateSummary[] | null>(null);
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<ChartTemplateSummary | null>(null);
  const [useTemplateSymbol, setUseTemplateSymbol] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchChartTemplates().then((found) => {
      if (live) setRows(found);
    });
    return () => {
      live = false;
    };
  }, []);

  const reload = async () => setRows(await fetchChartTemplates());

  /** Grouped the way every other picker here groups: mine, a club's, ours. */
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = (rows ?? []).filter(
      (r) =>
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        (r.category ?? "").toLowerCase().includes(needle) ||
        (r.symbol ?? "").toLowerCase().includes(needle),
    );
    return (
      [
        { kind: "user" as const, label: "Mine" },
        { kind: "org" as const, label: "Shared with me" },
        { kind: "system" as const, label: "From QuFolio" },
      ]
        .map((g) => ({ ...g, rows: matching.filter((r) => r.ownerKind === g.kind) }))
        .filter((g) => g.rows.length > 0)
    );
  }, [rows, query]);

  const moved = chosen
    ? movesLinkedCharts({
        useTemplateSymbol,
        templateSymbol: chosen.symbol,
        targetSymbol: group.symbol,
        symbolLinked,
        chartsInLayout,
      })
    : 0;

  const apply = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    setError(null);
    try {
      const full = await fetchChartTemplate(chosen.key);
      // **A template that cannot be read leaves the chart alone.** 404 covers
      // "deleted" and "no longer yours" alike, and the chart on screen is the
      // last good one — `applyScreenTo`'s rule for a screen.
      if (!full) {
        setError("That template is gone.");
        return;
      }
      onApply(applyTo(group, full.definition, { useTemplateSymbol }));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Delete one of your own, and drop it from the list on success only.
   *
   * Not optimistic: a write that failed must not look as though it worked, and
   * `deleteChartTemplate` throws with the server's sentence for exactly this.
   * Charts it was applied to are untouched — applying copied it.
   */
  const remove = async (row: ChartTemplateSummary) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteChartTemplate(row.key);
      setRows((current) => (current ?? []).filter((r) => r.key !== row.key));
      if (chosen?.key === row.key) {
        setChosen(null);
        setUseTemplateSymbol(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Drawings that come across, said plainly because they used to be lost.
   *
   * They move to the applied chart's price pane and stay stamped with the
   * symbol and interval they were made on, so they reappear on that symbol and
   * nowhere else. Stated rather than left silent: somebody who has drawn on a
   * chart wants to know what a template does to their lines *before* clicking,
   * and "nothing" is the answer worth giving.
   */
  const kept = chosen ? drawingsKept(group) : 0;

  return (
    <Backdrop onClose={onClose}>
      <div style={{ ...PANEL, width: 420 }}>
        <div style={HEAD}>Apply a chart template</div>
        <div style={BODY}>
          <input
            autoFocus
            aria-label="Search templates"
            placeholder="Search templates…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ ...INPUT, marginBottom: 8 }}
          />

          <div style={LIST}>
            {rows === null && <div style={EMPTY}>Loading…</div>}
            {rows !== null && groups.length === 0 && (
              <div style={EMPTY}>
                {rows.length === 0
                  ? "No templates yet — save one from a chart's right-click menu."
                  : "Nothing matches."}
              </div>
            )}
            {groups.map((g) => (
              <div key={g.kind}>
                <div style={GROUP_LABEL}>{g.label}</div>
                {g.rows.map((row) => (
                  <Fragment key={row.key}>
                  <div
                    style={row.key === chosen?.key ? { ...ROW_WRAP, ...ROW_ON } : ROW_WRAP}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setChosen(row);
                        // A template with no symbol has nothing to offer, so the
                        // choice below cannot be left on from a previous pick.
                        if (!row.symbol) setUseTemplateSymbol(false);
                      }}
                      style={ROW}
                    >
                      <span style={ROW_NAME}>{row.name}</span>
                      {/* Which group it is from, on the row itself: the heading
                          says "Shared with me" and a member of two groups
                          cannot tell whose is whose without it. */}
                      {row.ownerKind === "org" && (
                        <span style={GROUP_TAG}>from {row.ownerName ?? row.ownerKey}</span>
                      )}
                      <span style={ROW_META}>
                        {row.paneCount} pane{row.paneCount === 1 ? "" : "s"}
                        {row.symbol ? ` · ${row.symbol}` : ""}
                      </span>
                    </button>
                    {/* **Only on your own**, `LayoutPanel`'s rule for layout
                        templates: a club's is theirs and the product's is
                        everyone's, and the server answers 404 to a delete aimed
                        at either. A sibling of the row, not inside it — a
                        button in a button is not valid markup. */}
                    {authoringFor &&
                      row.ownerKind === "org" &&
                      row.ownerKey === authoringFor.group && (
                        <button
                          type="button"
                          aria-expanded={historyFor === row.key}
                          aria-label={`History of ${row.name}`}
                          onClick={() => setHistoryFor((was) => (was === row.key ? null : row.key))}
                          style={ROW_HISTORY}
                        >
                          {row.version !== undefined ? `v${row.version} ` : ""}History
                        </button>
                      )}
                    {row.ownerKind === "user" && (
                      <button
                        type="button"
                        aria-label={`Delete template ${row.name}`}
                        title="Delete this template — charts made from it are untouched"
                        disabled={busy}
                        onClick={() => void remove(row)}
                        style={ROW_DELETE}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {authoringFor && historyFor === row.key && (
                    <PublishedMenu
                      inline
                      kind="chart-templates"
                      presetKey={row.key}
                      groupName={authoringFor.name}
                      {...(row.version !== undefined ? { current: row.version } : {})}
                      onRestored={reload}
                      onClose={() => setHistoryFor(null)}
                    />
                  )}
                  </Fragment>
                ))}
              </div>
            ))}
          </div>

          {chosen?.symbol && (
            <div style={{ marginTop: 10 }}>
              <div style={SUBHEAD}>Symbol</div>
              <Tick
                radio
                on={!useTemplateSymbol}
                onToggle={() => setUseTemplateSymbol(false)}
                label={`Keep this chart's ${group.symbol}`}
              />
              <Tick
                radio
                on={useTemplateSymbol}
                onToggle={() => setUseTemplateSymbol(true)}
                label={`Use the template's ${chosen.symbol}`}
                hint={moved > 0 ? `Symbol is linked — moves all ${moved} charts.` : undefined}
                warn={moved > 0}
              />
            </div>
          )}

          {kept > 0 && (
            <div style={NOTE}>
              Your {kept} drawing{kept === 1 ? "" : "s"} on {group.symbol}{" "}
              {kept === 1 ? "is" : "are"} kept.
            </div>
          )}

          {error && <div style={ERROR}>{error}</div>}

          <div style={ACTIONS}>
            <button type="button" onClick={onClose} style={BUTTON}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void apply()}
              disabled={!chosen || busy}
              style={{ ...BUTTON, ...PRIMARY, ...(chosen && !busy ? {} : DISABLED) }}
            >
              {busy ? "Applying…" : "Apply to this chart"}
            </button>
          </div>
        </div>
      </div>
    </Backdrop>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Stopped, or an Escape that bubbled would close the chart's menu and
        // whatever is behind it as well.
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      style={BACKDROP}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={FIELD}>
      <span style={FIELD_LABEL}>{label}</span>
      {children}
    </label>
  );
}

function Tick({
  on,
  onToggle,
  label,
  hint,
  radio = false,
  warn = false,
}: {
  on: boolean;
  onToggle: () => void;
  label: string;
  hint?: string;
  radio?: boolean;
  warn?: boolean;
}) {
  return (
    <button type="button" role={radio ? "radio" : "checkbox"} aria-checked={on}
      onClick={onToggle} style={TICK}>
      <span style={{ ...BOX, ...(radio ? { borderRadius: "50%" } : {}), ...(on ? BOX_ON : {}) }} />
      <span style={{ flex: 1 }}>
        <span style={{ display: "block" }}>{label}</span>
        {hint && <span style={warn ? { ...HINT, ...HINT_WARN } : HINT}>{hint}</span>}
      </span>
    </button>
  );
}

const BACKDROP: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,.35)",
};

const PANEL: React.CSSProperties = {
  width: 380,
  maxWidth: "calc(100vw - 32px)",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 16px 44px rgba(0,0,0,.5)",
  color: "var(--qf-text)",
  fontSize: 12.5,
};

const HEAD: React.CSSProperties = {
  padding: "9px 14px",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  fontWeight: 600,
  borderRadius: "8px 8px 0 0",
};

const BODY: React.CSSProperties = { padding: 14 };

const FIELD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 9,
};

const FIELD_LABEL: React.CSSProperties = { flex: "0 0 68px", color: "var(--qf-text-secondary)" };

const INPUT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: "100%",
  font: "inherit",
  fontSize: 12.5,
  padding: "5px 9px",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
};

const TICK: React.CSSProperties = {
  display: "flex",
  gap: 9,
  alignItems: "flex-start",
  width: "100%",
  textAlign: "left",
  margin: "9px 0",
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  fontSize: 12.5,
  cursor: "pointer",
};

const BOX: React.CSSProperties = {
  flex: "0 0 13px",
  height: 13,
  marginTop: 2,
  border: "1px solid var(--qf-border)",
  borderRadius: 3,
  background: "var(--qf-background)",
};

const BOX_ON: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
};

const HINT: React.CSSProperties = {
  display: "block",
  color: "var(--qf-text-muted)",
  fontSize: 11.5,
  marginTop: 2,
};

const HINT_WARN: React.CSSProperties = { color: "var(--qf-warning, #d29922)" };

const SUBHEAD: React.CSSProperties = {
  color: "var(--qf-text-secondary)",
  marginBottom: 2,
};

const LIST: React.CSSProperties = {
  maxHeight: 220,
  overflowY: "auto",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "var(--qf-background)",
};

const GROUP_LABEL: React.CSSProperties = {
  padding: "5px 9px",
  fontSize: 10.5,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-muted)",
  background: "rgba(0,0,0,.18)",
};

const ROW_WRAP: React.CSSProperties = { display: "flex", alignItems: "center" };

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  padding: "5px 9px",
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  fontSize: 12.5,
  cursor: "pointer",
};

const ROW_ON: React.CSSProperties = { background: "var(--qf-selection)" };
const ROW_NAME: React.CSSProperties = { flex: 1, minWidth: 0 };
const ROW_META: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 11 };
const ROW_HISTORY: React.CSSProperties = {
  flex: "0 0 auto",
  padding: "2px 8px",
  border: 0,
  background: "none",
  color: "var(--qf-accent)",
  font: "inherit",
  fontSize: 11,
  cursor: "pointer",
};
const GROUP_TAG: React.CSSProperties = {
  flex: "0 0 auto",
  padding: "0 6px",
  borderRadius: 8,
  background: "var(--qf-surface)",
  color: "var(--qf-accent)",
  fontSize: 10,
  whiteSpace: "nowrap",
};

const ROW_DELETE: React.CSSProperties = {
  flex: "0 0 auto",
  padding: "2px 9px",
  border: 0,
  background: "none",
  color: "var(--qf-negative)",
  font: "inherit",
  fontSize: 14,
  lineHeight: 1,
  cursor: "pointer",
};

const EMPTY: React.CSSProperties = { padding: 10, color: "var(--qf-text-muted)" };

const NOTE: React.CSSProperties = {
  color: "var(--qf-text-muted)",
  fontSize: 11.5,
  marginTop: 10,
};

const ERROR: React.CSSProperties = {
  marginTop: 10,
  fontSize: 11.5,
  color: "var(--qf-negative)",
};

const ACTIONS: React.CSSProperties = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
  marginTop: 14,
};

const BUTTON: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "4px 11px",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
  fontWeight: 600,
};

const DISABLED: React.CSSProperties = { opacity: 0.5, cursor: "default" };

/** "Replace" carries the template's name, which can be long; Cancel must stay on the row. */
const LABEL_FITS: React.CSSProperties = {
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
